/**
 * AudioEngine that drives the native playback service through the control-only bridge.
 *
 * No audio crosses this class. It compiles a frame envelope, asks the service to cache
 * and play it, and surfaces status as the same events the web engine emits.
 */

import type { Recording, SessionPlan } from '../types.ts';
import { assetOrigin } from '../config/origin.ts';
import type { AudioEngine, EngineEvent } from './engine.ts';
import type { PlaybackIntent } from './lifecycle.ts';
import {
  PLAYER_STATE_ENDED,
  type BrahmBridge,
  type PlaybackStatus,
} from './native-bridge.ts';
import { FRAME_RATE, secondsToFrame } from './native-protocol.ts';
import { compileEnvelope } from './native-schedule.ts';
import { ToneProfile } from './tone.ts';
import { ByteStore } from './sources.ts';

const DEFAULT_POLL_MS = 250;

/**
 * How long a `play` may keep asking while the controller insists nothing is loaded, and
 * how often. The budget is short on purpose: past it, the contradiction is real and the
 * person is owed the error rather than a spinner.
 */
const PLAY_RETRY_BUDGET_MS = 2000;
const PLAY_RETRY_INTERVAL_MS = 150;

/** The plugin's stable code for it; the message is matched too, since Capacitor's
 *  rejection shape has changed across major versions and the code is what is contractual. */
function isStaleSessionError(err: unknown): boolean {
  const e = err as { code?: unknown; message?: unknown } | null;
  if (e && typeof e.code === 'string' && e.code === 'E_NO_SESSION') return true;
  return !!e && typeof e.message === 'string' && e.message === 'No playback session is loaded';
}

export class NativeAudioEngine implements AudioEngine {
  private listeners = new Set<(e: EngineEvent) => void>();
  private generation = 0;
  private loaded = false;
  private isPlaying = false;
  private plan: SessionPlan | null = null;
  private totalFrameCount = 0;
  private lastFrame = 0;
  private lastBufferedFrame = 0;
  private lastPlayWhenReady: boolean | null = null;
  private lastError: string | null = null;
  private endedEmitted = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private delayTimer: ReturnType<typeof setTimeout> | null = null;
  private delayResolve: (() => void) | null = null;
  private pollInFlight = false;
  private transportRevision = 0;
  private pendingTransportCommands = 0;

  constructor(
    private readonly bridge: BrahmBridge,
    private readonly opts: {
      manifestVersion: string; budgetBytes: number; pollMs?: number;
      /** Injected only by tests, so a two-second retry budget costs no wall time. */
      now?: () => number;
    },
  ) {}

  private get pollMs(): number {
    return this.opts.pollMs ?? DEFAULT_POLL_MS;
  }

  private now(): number {
    return this.opts.now ? this.opts.now() : Date.now();
  }

  get duration(): number {
    return this.plan?.actualSec ?? 0;
  }

  get currentTime(): number {
    // Last polled frame only. Interpolating with wall time between polls would be a
    // number this app did not measure.
    return this.lastFrame / FRAME_RATE;
  }

  get playing(): boolean {
    return this.isPlaying;
  }

  get scheduledThrough(): number {
    return this.lastBufferedFrame / FRAME_RATE;
  }

  on(fn: (e: EngineEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  async load(rec: Recording, plan: SessionPlan): Promise<void> {
    const generation = ++this.generation;
    this.stopPolling();
    this.clearDelay();
    this.loaded = false;
    this.isPlaying = false;
    this.plan = plan;
    this.lastFrame = 0;
    this.lastBufferedFrame = 0;
    this.lastPlayWhenReady = null;
    this.lastError = null;
    this.endedEmitted = false;
    this.totalFrameCount = 0;

    try {
      let targetRms: number | undefined;
      if (rec.toneProfile) {
        const url = `${assetOrigin()}/audio/${rec.toneProfile}`;
        // Keep the small profile in the WebView cache too. The native service owns playback
        // files, but compilation needs its measured RMS before an envelope exists; retaining
        // these 4 KB makes a later airplane-mode restart compilable without moving audio over
        // Binder.
        try {
          targetRms = (await this.bridge.profileRms({
            path: rec.toneProfile,
            manifestVersion: this.opts.manifestVersion,
          })).rms;
        } catch {
          const bytes = await new ByteStore().fetch(url, this.opts.budgetBytes);
          if (generation !== this.generation) return;
          targetRms = ToneProfile.fromBytes(bytes).rms;
        }
        if (generation !== this.generation) return;
      }

      const envelope = compileEnvelope(rec, plan, {
        manifestVersion: this.opts.manifestVersion,
        targetRms,
      });
      if (generation !== this.generation) return;
      this.totalFrameCount = envelope.totalFrameCount;

      const prepared = await this.bridge.prepare({
        envelopeJson: JSON.stringify(envelope),
        budgetBytes: this.opts.budgetBytes,
      });
      if (generation !== this.generation) return;

      const cached = await this.waitUntilCached(generation);
      if (cached === 'abandoned' || generation !== this.generation) return;

      await this.bridge.load({
        envelopeSha256: prepared.envelopeSha256,
        title: rec.title,
        subtitle: rec.recordedOn,
      });
      if (generation !== this.generation) return;
      this.loaded = true;
      for (const op of envelope.ops) {
        if (op.source.kind === 'tone' && op.prov.kind === 'synth') {
          this.emit({ type: 'tone_level', gap: op.prov.gap, chunk: op.prov.chunk,
            atSec: op.atFrame / envelope.sampleRate, targetRms: op.source.targetRms });
        }
      }
      // Polling begins at load, not at play, and does not stop when playback stops. The
      // whole point of the Media3 session is that a person can press play on the
      // notification or the lock screen — and the service will start playing while this
      // side, having stopped polling on pause, goes on showing "paused" forever. A status
      // call is a Binder round trip inside one device, and a backgrounded WebView has its
      // timers throttled by the browser anyway.
      this.startPolling();
    } catch (err) {
      if (generation !== this.generation) return;
      throw err;
    }
  }

  /**
   * `E_NO_SESSION` after `load()` has already resolved is a contradiction, not an answer.
   *
   * The service sets its media item inside the LOAD command and only then returns success,
   * so by the time `load()` resolves the *session* is loaded. `play` is rejected by
   * `BrahmPlugin.playerCommand(requireSession = true)`, which reads
   * `MediaController.mediaItemCount` — and that is the Activity-side **replica** of the
   * session's state, delivered by a separate message from the one that carried the command
   * result. Nothing on either side of the bridge waits for or observes that replica: the
   * plugin registers no `Player.Listener`, so a controller that has not yet been told about
   * the media item answers zero and the sit is refused outright.
   *
   * Measured on Guy's phone 2026-09-14, the only native sit on record at the time: two
   * Begin taps refused with this code, 3.5 s and 0.06 s after their session opened, and the
   * third started cleanly and ran the full twenty minutes. It did not reproduce on a clean
   * emulator, so this is not the deterministic first-launch case and the trigger is still
   * unknown — but every candidate cause is a controller whose view is momentarily behind
   * the service's, and against that a bounded retry is the correct answer rather than a
   * papering-over. Anything else still throws on the first attempt.
   *
   * The retry is visible: it emits `play_retry` so a trace shows it happened rather than
   * hiding a defect behind a delay, and it stops the moment `destroy()` bumps the
   * generation, so an abandoned sit cannot keep asking.
   */
  async play(): Promise<void> {
    if (!this.loaded) throw new Error('play() before load()');
    if (this.isPlaying) return;
    const generation = this.generation;
    const deadline = this.now() + PLAY_RETRY_BUDGET_MS;
    const transportRevision = ++this.transportRevision;
    this.pendingTransportCommands++;
    try {
      for (let attempt = 0; ; attempt++) {
        if (generation !== this.generation || !this.loaded
            || transportRevision !== this.transportRevision) return;
        try {
          await this.bridge.play();
          break;
        } catch (err) {
          if (generation !== this.generation || !this.loaded
              || transportRevision !== this.transportRevision) return;
          if (!isStaleSessionError(err)) throw err;
          if (this.now() >= deadline) throw err;
          this.emit({ type: 'play_retry', attempt: attempt + 1 });
          await this.delay(PLAY_RETRY_INTERVAL_MS);
          if (generation !== this.generation || !this.loaded
              || transportRevision !== this.transportRevision) return;
        }
      }
    } finally {
      this.pendingTransportCommands--;
    }
    if (generation !== this.generation || transportRevision !== this.transportRevision) return;
    this.isPlaying = true;
    this.lastPlayWhenReady = true;
    this.startPolling();
    this.emit({ type: 'play' });
  }

  async recover(): Promise<void> {
    if (!this.loaded) throw new Error('recover() before load()');
    let status: PlaybackStatus;
    try {
      status = await this.bridge.status();
    } catch {
      throw new Error('native playback service unreachable');
    }
    if (status.error !== null) throw new Error(status.error);
    this.lastFrame = status.frame;
    this.lastBufferedFrame = status.bufferedFrame;
    this.lastPlayWhenReady = status.playWhenReady;
    // Re-read only. Restarting or re-loading would resume audio a person did not ask for.
  }

  inspectClock(_intent: PlaybackIntent): void {
    // No AudioContext, so no clock that can drift against an audio thread.
  }

  pause(): void {
    if (!this.loaded) return;
    const generation = this.generation;
    const transportRevision = ++this.transportRevision;
    this.pendingTransportCommands++;
    void this.bridge.pause().then(
      () => this.finishTransportCommand(),
      () => {
        this.finishTransportCommand();
        // The optimistic pause below is corrected from the service's measured state. If
        // STATUS also fails, its normal stalled event explains that the service is gone.
        if (generation === this.generation && transportRevision === this.transportRevision) {
          void this.pollPlayback();
        }
      },
    );
    if (this.isPlaying) {
      this.isPlaying = false;
      this.lastPlayWhenReady = false;
      this.emit({ type: 'pause' });
    }
  }

  seek(sec: number): void {
    if (!this.loaded) return;
    const frame = Math.max(0, Math.min(secondsToFrame(sec), this.totalFrameCount));
    this.lastFrame = frame;
    void this.bridge.seekToFrame({ frame });
    this.emit({ type: 'timeupdate', currentTime: this.currentTime, duration: this.duration });
  }

  setVolume(_v: number): void {
    // The service owns its own fades and the device owns its volume. There is no
    // AudioContext here, so there is no gain node to ramp.
  }

  destroy(): void {
    this.generation++;
    this.loaded = false;
    this.isPlaying = false;
    this.clearDelay();
    this.stopPolling();
    this.listeners.clear();
    void this.bridge.stop();
  }

  private emit(e: EngineEvent): void {
    for (const fn of this.listeners) {
      try { fn(e); } catch { /* a bad listener must not stop playback */ }
    }
  }

  private async waitUntilCached(generation: number): Promise<PlaybackStatus | 'abandoned'> {
    for (;;) {
      if (generation !== this.generation) return 'abandoned';
      const status = await this.bridge.status();
      if (generation !== this.generation) return 'abandoned';
      this.lastFrame = status.frame;
      this.lastBufferedFrame = status.bufferedFrame;
      this.lastPlayWhenReady = status.playWhenReady;
      this.emit({
        type: 'cache_progress',
        readyBytes: status.cacheReadyBytes,
        totalBytes: status.cacheTotalBytes,
      });
      if (status.cacheState === 'ready') return status;
      if (status.cacheState === 'error') {
        throw new Error(status.error ?? 'error');
      }
      await this.delay(this.pollMs);
    }
  }

  private delay(ms: number): Promise<void> {
    this.clearDelay();
    return new Promise((resolve) => {
      this.delayResolve = resolve;
      this.delayTimer = setTimeout(() => {
        this.delayTimer = null;
        this.delayResolve = null;
        resolve();
      }, ms);
    });
  }

  private clearDelay(): void {
    if (this.delayTimer !== null) {
      clearTimeout(this.delayTimer);
      this.delayTimer = null;
    }
    if (this.delayResolve) {
      const resolve = this.delayResolve;
      this.delayResolve = null;
      resolve();
    }
  }

  private startPolling(): void {
    this.stopPolling();
    void this.pollPlayback();
    this.pollTimer = setInterval(() => { void this.pollPlayback(); }, this.pollMs);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async pollPlayback(): Promise<void> {
    if (this.pollInFlight) return;
    this.pollInFlight = true;
    const generation = this.generation;
    const transportRevision = this.transportRevision;
    const commandWasPending = this.pendingTransportCommands !== 0;
    const requestedAtMs = performance.now();
    try {
      const status = await this.bridge.status();
      const receivedAtMs = performance.now();
      if (generation !== this.generation || commandWasPending
          || transportRevision !== this.transportRevision
          || this.pendingTransportCommands !== 0) return;
      // Media3 exposes this position through a millisecond axis (up to 1 ms quantisation)
      // and the bridge reply can have been observed anywhere inside the round trip.
      this.applyPlaybackStatus(status, receivedAtMs - requestedAtMs + 1);
    } catch (err) {
      if (generation !== this.generation || commandWasPending
          || transportRevision !== this.transportRevision
          || this.pendingTransportCommands !== 0) return;
      const reason = err instanceof Error ? err.message : 'status';
      this.emit({ type: 'stalled', reason });
    } finally {
      this.pollInFlight = false;
    }
  }

  private finishTransportCommand(): void {
    this.pendingTransportCommands--;
  }

  private applyPlaybackStatus(status: PlaybackStatus, uncertaintyMs: number): void {
    this.lastFrame = status.frame;
    this.lastBufferedFrame = status.bufferedFrame;

    const prevPlay = this.lastPlayWhenReady;
    this.lastPlayWhenReady = status.playWhenReady;
    if (prevPlay !== null && prevPlay !== status.playWhenReady) {
      this.isPlaying = status.playWhenReady;
      this.emit({ type: status.playWhenReady ? 'play' : 'pause' });
    }

    this.emit({
      type: 'timeupdate',
      currentTime: this.currentTime,
      duration: this.duration,
      clock: {
        domain: 'native_output',
        playbackFrame: status.frame,
        sampleRate: FRAME_RATE,
        observedAtMs: status.observedAtElapsedRealtimeNanos / 1_000_000,
        uncertaintyMs,
      },
    });

    if (status.playbackState === PLAYER_STATE_ENDED && !this.endedEmitted) {
      this.endedEmitted = true;
      this.isPlaying = false;
      this.emit({ type: 'ended' });
    }

    if (status.error !== null && status.error !== this.lastError) {
      this.lastError = status.error;
      this.emit({ type: 'stalled', reason: status.error });
    }
  }
}
